import { useMemo, useState } from 'react';
import { createOrUpdateMarkupSchema, MarkupsTable } from '@retail/markup/components';
import { ConditionsNew } from '@retail/calculus/types';
import { OutlinedButton, UniversalFormProvider } from '@shared/components';
import CreateMarkupCondition from './CreateMarkupCondition';
import { AddButton, CalculusActionPortal, DeleteDialog } from '@retail/components';
import { usePermissionsForCurrentUser, useUserBrands } from '@retail/app/stores/selected-context';
import { Stack } from '@mui/material';
import { useDisclosure } from '@shared/hooks';
import { ExportMenu } from '@retail/calculus/components';
import { useAppTFunction } from '@retail/app/i18n';
import { SupplierBase } from '@retail/suppliers/types';
import { useConditionActions } from '@retail/calculus/context';
import { UploadMarkupConditionsDialog, useMarkupExport } from './UploadMarkupConditions';
import { EditMarkupDialog } from './EditMarkupDialog';

interface Props {
  conditions: ConditionsNew[];
  isFetching: boolean;
  selectedSupplier?: SupplierBase;
}

export function MarkupConditionContainer({ conditions, isFetching, selectedSupplier }: Props) {
  const t = useAppTFunction();
  const { selectedBrand } = useUserBrands();

  const { exportAsCsv, exportAsXLSX } = useMarkupExport();
  const { removeCondition } = useConditionActions();

  const { isOpen: isUploadOpen, onToggle: toggleUploadDialog } = useDisclosure();
  const { isOpen: isCreateOpen, onToggle: toggleCreateDialog } = useDisclosure();

  const { userCanCreate, userCanUpdate } = usePermissionsForCurrentUser();
  const userCanCreateMarkups = userCanCreate('PRICE_CALCULATION__MARKUP');
  const userCanUpdateMarkups = userCanUpdate('PRICE_CALCULATION__MARKUP');

  const [editCondition, setEdit] = useState<ConditionsNew | undefined>();
  const [deactivateConditionId, setDeactivate] = useState<number | undefined>();
  const [deleteConditionId, setDelete] = useState<number | undefined>();

  const conditionActions = userCanUpdateMarkups ? { setEdit, setDeactivate, setDelete } : undefined;

  const includeUnit = useMemo(() => selectedBrand.wholesaler, [selectedBrand.wholesaler]);

  const supplierLevelMarkupConditions = useMemo(
    () => conditions.filter((condition) => condition.bindingType !== 'ASSORTMENT') ?? [],
    [conditions]
  );

  const markupSchema = useMemo(() => createOrUpdateMarkupSchema(t, includeUnit), [includeUnit, t]);

  return (
    <Stack flexGrow={1}>
      <CalculusActionPortal>
        <ExportMenu
          exportDataAsCsv={() => exportAsCsv(supplierLevelMarkupConditions)}
          exportDataAsExcel={() => exportAsXLSX(supplierLevelMarkupConditions)}
        />

        {selectedSupplier && (
          <OutlinedButton
            color="secondary"
            size="small"
            onClick={toggleUploadDialog}
            disabled={!userCanCreateMarkups}
          >
            {t('calculus.actions.uploadMarkup')}
          </OutlinedButton>
        )}

        <AddButton onClick={toggleCreateDialog} disabled={!userCanCreateMarkups}>
          {t('calculus.createMarkup')}
        </AddButton>
      </CalculusActionPortal>

      <MarkupsTable
        loading={isFetching}
        conditions={conditions}
        conditionActions={conditionActions}
        supplierLevel={!!selectedSupplier}
        openCreateDialog={userCanCreateMarkups ? toggleCreateDialog : undefined}
      />

      {userCanCreateMarkups && isCreateOpen && (
        <UniversalFormProvider schema={markupSchema} defaultValues={{ ...markupSchema.getDefault(), level: selectedSupplier ? '' : 'assortment' }}>
          <CreateMarkupCondition
            open={isCreateOpen}
            onClose={toggleCreateDialog}
            supplierNumber={selectedSupplier?.supplierNumber}
            includeUnit={includeUnit}
          />
        </UniversalFormProvider>
      )}

      {selectedSupplier && userCanCreateMarkups && (
        <UploadMarkupConditionsDialog
          open={isUploadOpen}
          onClose={toggleUploadDialog}
          mgSupplierId={selectedSupplier.supplierNumber}
        />
      )}

      {editCondition && (
        <EditMarkupDialog isOpen markupToEdit={editCondition} onClose={() => setEdit(undefined)} />
      )}

      {deactivateConditionId && (
        <DeleteDialog
          isOpen
          dialogTitle={t('calculus.actions.deactivateCondition')}
          message={t('calculus.actions.confirmDeactivation')}
          onDelete={() => removeCondition([deactivateConditionId], 'delete')}
          onClose={() => setDeactivate(undefined)}
        />
      )}
      {deleteConditionId && (
        <DeleteDialog
          isOpen
          dialogTitle={t('calculus.actions.deleteFromDraft')}
          message={t('calculus.actions.confirmRemoval')}
          onDelete={() => removeCondition([deleteConditionId], 'delete')}
          onClose={() => setDelete(undefined)}
        />
      )}
    </Stack>
  );
}
